import React, { useEffect, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { InputField } from '../ds/InputField';
import { statusLabel } from '../ds/StatusMap';
import { ceebEditIssuedApi } from '../services/api';
import type { CeebEditIssuedCheck, CeebSnapshot } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { normCaseNumber, snapshotPhaseLabel } from '../utils/ceebEditIssued';
import { SnapshotPdfButton } from './CeebEditIssuedStatus';

/**
 * Potwierdzenie POPRAWKI wystawionego protokołu w CEEB (superadmin, decyzja właściciela 2026-10-09).
 *
 * Protokół „Wprowadzony" jest już dokumentem w rejestrze państwowym, więc modal:
 *  - najpierw pyta serwer o bramkę (`ceeb-edit-issued-check`) i pokazuje fakty sprawy — `allowed:false`
 *    = gotowy polski komunikat serwera i brak przycisku potwierdzenia;
 *  - pokazuje, co zmienimy (lokale z danymi z terenu, z możliwością zawężenia) i czego nie ruszymy;
 *  - wymaga świadomego potwierdzenia: pole „Rozumiem…" + przepisany numer sprawy;
 *  - domyślnie proponuje próbę bez zapisu przy pierwszym przebiegu;
 *  - pokazuje kopie protokołu z CEEB zapisane przy wcześniejszych przebiegach (PDF).
 * Błąd startu (409/400) zostaje w modalu; 202 → `onStarted`, a wołający zamyka modal i podpina polling.
 */
export interface CeebEditIssuedTarget {
  buildingId: string;
  address: string;
}

interface Props {
  target: CeebEditIssuedTarget | null;
  onCancel: () => void;
  onStarted: (jobId: string, dryRun: boolean) => void;
}

const box = (tone: 'deny' | 'info'): React.CSSProperties => ({
  margin: 'var(--space-3) 0 0',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: `var(--border-width) solid ${tone === 'deny' ? 'var(--status-deny-bar)' : 'var(--status-rec-bar)'}`,
  background: tone === 'deny' ? 'var(--status-deny-bg)' : 'var(--status-rec-bg)',
  color: 'var(--text-primary)',
  fontSize: 'var(--text-caption)',
});

const sectionTitle: React.CSSProperties = {
  margin: 'var(--space-4) 0 var(--space-2)',
  fontSize: 'var(--text-label)',
  fontWeight: 'var(--weight-semibold)',
  color: 'var(--text-heading)',
};

const checkRow: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  alignItems: 'flex-start',
  cursor: 'pointer',
};

const muted: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' };

const LAST_RUN_STATUS: Record<string, string> = { running: 'w toku', done: 'zakończony', error: 'przerwany' };
/** Ile najnowszych kopii pokazać w modalu (pełna lista: GET /buildings/:id/ceeb-snapshots). */
const SNAPSHOTS_SHOWN = 4;

export default function CeebEditIssuedModal({ target, onCancel, onStarted }: Props) {
  const [check, setCheck] = useState<CeebEditIssuedCheck | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<CeebSnapshot[] | null>(null);
  const [snapshotsError, setSnapshotsError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [dryRun, setDryRun] = useState(true);
  const [understood, setUnderstood] = useState(false);
  const [typedCase, setTypedCase] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const buildingId = target?.buildingId ?? null;

  useEffect(() => {
    setCheck(null);
    setLoadError(null);
    setSnapshots(null);
    setSnapshotsError(null);
    setSelected([]);
    setDryRun(true);
    setUnderstood(false);
    setTypedCase('');
    setStartError(null);
    if (!buildingId) return undefined;
    let aktualne = true;
    ceebEditIssuedApi.check(buildingId)
      .then((c) => {
        if (!aktualne) return;
        setCheck(c);
        setSelected((c.pending || []).map((p) => p.nr));
        // Pierwszy przebieg, brak udanej próby (serwer i tak odmówi) albo serwer nie powiedział → próba bez zapisu.
        setDryRun(c.firstRun !== false || c.dryRunRequired === true);
      })
      .catch((e) => { if (aktualne) setLoadError(e instanceof Error ? e.message : 'Nie udało się sprawdzić protokołu'); });
    // Kopie to wzbogacenie modala — ich błąd nie blokuje poprawki (bramka i tak wymaga świeżej kopii przed zmianą).
    ceebEditIssuedApi.snapshots(buildingId)
      .then((r) => { if (aktualne) setSnapshots(Array.isArray(r?.snapshots) ? r.snapshots : []); })
      .catch((e) => { if (aktualne) setSnapshotsError(e instanceof Error ? e.message : 'Nie udało się pobrać listy kopii protokołu'); });
    return () => { aktualne = false; };
  }, [buildingId]);

  const pending = check?.pending || [];
  const caseNumber = normCaseNumber(check?.caseNumber);
  const typedNorm = normCaseNumber(typedCase);
  const caseMatches = !!caseNumber && typedNorm === caseNumber;
  const allowed = !!check && check.allowed;
  const canConfirm = allowed && understood && caseMatches && selected.length > 0 && !starting;

  const toggleLokal = (nr: string, on: boolean) => {
    setSelected((prev) => (on ? [...prev.filter((x) => x !== nr), nr] : prev.filter((x) => x !== nr)));
  };

  const submit = async () => {
    if (!target || !check || !canConfirm) return;
    setStarting(true);
    setStartError(null);
    try {
      // Zawsze jawna lista lokali z modala: lokal, który doszedł do kolejki po otwarciu, nie wejdzie bez wiedzy operatora.
      const onlyLokale = pending.map((p) => p.nr).filter((nr) => selected.includes(nr));
      const res = await ceebEditIssuedApi.start(target.buildingId, { caseNumber: typedNorm, dryRun, onlyLokale });
      onStarted(res.jobId, typeof res.dryRun === 'boolean' ? res.dryRun : dryRun);
    } catch (e) {
      setStartError(e instanceof Error ? e.message : 'Nie udało się uruchomić poprawki protokołu');
    } finally {
      setStarting(false);
    }
  };

  const lastRun = check?.lastRun || null;
  const untouched = check?.untouched || [];
  const fileStem = `kopia_protokolu_${caseNumber.replace(/\W+/g, '_') || 'ceeb'}`;

  return (
    <ModalShell
      open={!!target}
      title={target ? `Poprawić wystawiony protokół w CEEB — ${check?.address || target.address}?` : undefined}
      onClose={() => { if (!starting) onCancel(); }}
      width="min(92vw, 600px)"
      footer={(
        <>
          <Button variant="secondary" disabled={starting} onClick={onCancel}>Anuluj</Button>
          {allowed && (
            <Button
              variant="primary"
              icon="pencil"
              data-testid="ceeb-edit-issued-confirm"
              disabled={!canConfirm}
              style={canConfirm && !dryRun ? { background: 'var(--status-rec-bar)', borderColor: 'var(--status-rec-bar)' } : undefined}
              onClick={() => { void submit(); }}
            >
              {starting ? 'Uruchamianie…' : dryRun ? 'Popraw protokół w CEEB (próba bez zapisu)' : 'Popraw protokół w CEEB'}
            </Button>
          )}
        </>
      )}
    >
      {!check && !loadError && <p style={{ margin: 0, color: 'var(--text-muted)' }}>Sprawdzam protokół…</p>}
      {loadError && <div role="alert" data-testid="ceeb-edit-issued-load-error" style={{ ...box('deny'), margin: 0 }}>{loadError}</div>}
      {check && (
        <div data-testid="ceeb-edit-issued-check">
          <p style={{ margin: 0 }}>
            {`Sprawa ${check.caseNumber || '—'} · status w CEEB: ${check.ceebStatus || 'nieznany'} · rok ${check.caseYear ?? '—'}`}
          </p>

          {!check.allowed && (
            <div role="alert" data-testid="ceeb-edit-issued-blocked" style={box('deny')}>
              {check.error || 'Poprawka tego protokołu jest teraz niedostępna.'}
            </div>
          )}

          {check.allowed && (
            <>
              <h3 style={sectionTitle}>Co zmienimy</h3>
              <p style={{ ...muted, margin: '0 0 var(--space-2)' }}>
                Bot wpisze do protokołu dane z terenu zaznaczonych lokali.
              </p>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                {pending.map((p) => (
                  <li key={p.nr} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <label style={checkRow}>
                      <input
                        type="checkbox"
                        checked={selected.includes(p.nr)}
                        disabled={starting}
                        onChange={(e) => toggleLokal(p.nr, e.target.checked)}
                      />
                      <span>{`Lokal ${p.nr}`}</span>
                    </label>
                    {p.status && <span style={muted}>{statusLabel(p.status)}</span>}
                  </li>
                ))}
              </ul>
              {selected.length === 0 && (
                <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--status-deny)', fontSize: 'var(--text-caption)' }}>
                  Zaznacz co najmniej jeden lokal.
                </p>
              )}

              {untouched.length > 0 && (
                <>
                  <h3 style={sectionTitle}>Czego nie ruszymy</h3>
                  <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', color: 'var(--text-secondary)' }}>
                    {untouched.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                </>
              )}

              <div style={{ ...box('info'), display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
                <span style={{ display: 'inline-flex', color: 'var(--status-rec)' }}><Icon name="lock" size={16} /></span>
                <span>Przed zmianą zapiszemy pełną kopię protokołu z CEEB. Bez udanej kopii bot niczego nie zmieni.</span>
              </div>

              {lastRun && (
                <p style={{ ...muted, margin: 'var(--space-3) 0 0' }}>
                  {`Ostatni przebieg: ${lastRun.dryRun ? 'próba bez zapisu' : 'poprawka'} · ${LAST_RUN_STATUS[lastRun.status] || lastRun.status}`}
                  {lastRun.finishedAt ? ` · ${formatDate(lastRun.finishedAt)}` : ''}
                </p>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                <label style={checkRow}>
                  <input
                    type="checkbox"
                    checked={dryRun}
                    disabled={starting || check.dryRunRequired === true}
                    onChange={(e) => setDryRun(e.target.checked)}
                  />
                  <span>Najpierw próba bez zapisu</span>
                </label>
                {check.dryRunRequired === true && (
                  <p style={{ margin: 0, color: 'var(--status-rec)', fontSize: 'var(--text-caption)' }}>
                    Prawdziwa poprawka będzie możliwa po udanej próbie bez zapisu dla tego protokołu.
                  </p>
                )}
                {dryRun ? (
                  <p style={{ ...muted, margin: 0 }}>
                    Bot przejdzie przez protokół bez zapisywania — w CEEB nic się nie zmieni.
                  </p>
                ) : check.firstRun !== false && (
                  <p style={{ margin: 0, color: 'var(--status-rec)', fontSize: 'var(--text-caption)' }}>
                    To pierwszy przebieg dla tego protokołu — bezpieczniej zacząć od próby bez zapisu.
                  </p>
                )}
                <label style={checkRow}>
                  <input
                    type="checkbox"
                    checked={understood}
                    disabled={starting}
                    onChange={(e) => setUnderstood(e.target.checked)}
                  />
                  <span>Rozumiem, że zmieniam dokument już wystawiony w rejestrze CEEB</span>
                </label>
                <InputField
                  label="Przepisz numer sprawy"
                  value={typedCase}
                  disabled={starting}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => setTypedCase(e.target.value)}
                  errorMessage={typedNorm.length >= caseNumber.length && typedNorm && !caseMatches
                    ? 'Numer nie zgadza się z numerem sprawy'
                    : undefined}
                />
              </div>
            </>
          )}

          {(snapshotsError || (snapshots && snapshots.length > 0)) && (
            <>
              <h3 style={sectionTitle}>Kopie protokołu z CEEB</h3>
              {snapshotsError && <p role="alert" style={{ margin: 0, color: 'var(--status-deny)', fontSize: 'var(--text-caption)' }}>{snapshotsError}</p>}
              <ul data-testid="ceeb-edit-issued-snapshots" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                {(snapshots || []).slice(0, SNAPSHOTS_SHOWN).map((s) => (
                  <li key={s.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 'var(--space-2)' }}>
                    <span style={muted}>
                      {`${formatDate(s.takenAt)} · ${snapshotPhaseLabel(s.phase)}${s.protocolStatus ? ` · ${s.protocolStatus}` : ''}`}
                      {s.complete ? '' : ' · niekompletna'}
                    </span>
                    {s.hasPdf && (
                      <SnapshotPdfButton
                        buildingId={buildingId ?? ''}
                        snapshotId={s.id}
                        label="PDF"
                        fallbackName={`${fileStem}_${s.phase}.pdf`}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {startError && (
            <div role="alert" data-testid="ceeb-edit-issued-start-error" style={box('deny')}>{startError}</div>
          )}
        </div>
      )}
    </ModalShell>
  );
}
